// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { shd } from '@shd/jslib/models';
import { Avatar, Box, Button, Card, Image, Text } from 'grommet';
import React, { useEffect, useState } from 'react';
import axios from '../../helpers/axios';
import SHDLink from '../../helpers/SHDLink';
import EditPlayerProfile from '../Player/EditPlayerProfile';
import { useHistory } from 'react-router-dom';

// Define enums for sports and player positions
enum SportType {
  BASEBALL = 'Baseball',
  BASKETBALL = 'Basketball',
  SOCCER = 'Soccer',
  VOLLEYBALL = 'Volleyball',
  WATER_POLO = 'Water Polo',
  SOFTBALL = 'Softball',
  HOCKEY = 'Hockey',
  OTHER = 'Other',
}

interface Props {
  player: shd.ClaimedPlayer;
  isAdmin: boolean;
}

// Utility function to get primary position based on sport type
const getPrimaryPosition = (
  player: shd.ClaimedPlayer,
  sportType: SportType
): string => {
  switch (sportType) {
    case SportType.BASEBALL:
    case SportType.SOFTBALL:
      return player.positionPrimary || '-';
    case SportType.BASKETBALL:
      return player.positionPrimaryBasketball || '-';
    case SportType.SOCCER:
      return player.positionPrimarySoccer || '-';
    case SportType.VOLLEYBALL:
      return player.positionPrimaryVB || '-';
    default:
      return '-';
  }
};

const ClaimedPlayerCard: React.FC<Props> = ({ player, isAdmin }) => {
  const [fullPlayer, setFullPlayer] = useState<shd.ClaimedPlayer>(player);
  const [teams, setTeams] = useState<shd.Team[]>([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const history = useHistory();

  const isDKPaired = Boolean(fullPlayer.dkPlayerUuid);

  // Fetch full player data and teams when the component is mounted or when the player ID changes
  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const response = await axios.get(`/api/player/${player._id}`);
        if (response?.data) {
          setFullPlayer(response.data.player);
          setTeams(response.data.teams);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch player data:', error);
      }
    };
    fetchPlayer();
  }, [player._id]);

  // Get the primary position based on the player's primary team and sport type
  const getTitle = () => {
    if (!fullPlayer || !teams.length) {
      return '-';
    }

    // Get the highest priority team ID
    const primaryTeamId = fullPlayer.DNOR_teamIdPriority?.[0];
    if (!primaryTeamId) {
      return '-';
    }

    // Find the corresponding team from the teams array
    const primaryTeam = teams.find((team) => team._id === primaryTeamId);
    if (!primaryTeam) {
      return '-';
    }

    // Get the primary position based on the sport type of the primary team
    return getPrimaryPosition(
      fullPlayer,
      primaryTeam.attrib_sportType as SportType
    );
  };

  const getSubtitle = () => {
    if (fullPlayer.yearGraduation) {
      return `Class of ${fullPlayer.yearGraduation}`;
    } else if (fullPlayer.collegeCommitName) {
      return `Committed: ${fullPlayer.collegeCommitName}`;
    }
    return '';
  };

  return (
    <Card
      data-testid="claimed-player-card"
      pad="medium"
      elevation="small"
      gap="large"
    >
      <Box
        direction="row"
        gap="small"
        flex={{ grow: 1, shrink: 0 }}
        fill="horizontal"
      >
        <SHDLink to={`/${player._id}`}>
          {fullPlayer.DNOR_imgUrlT150 ? (
            <Avatar src={fullPlayer.DNOR_imgUrlT150} size="medium" />
          ) : (
            <Avatar background="gray-2" size="medium"></Avatar>
          )}
        </SHDLink>
        <SHDLink
          to={`/${player._id}`}
          style={{
            flex: '1 1',
            minWidth: '0px',
          }}
        >
          <Box
            data-testid={`claimed-player-${fullPlayer.nameFirst}`}
            gap="xsmall"
            flex
          >
            <Box direction="row" gap="small" align="center">
              <Text weight={'bold'} truncate>
                {fullPlayer.nameFirst} {fullPlayer.nameLast}
              </Text>
              {isDKPaired && (
                <Box height="16px" width="16px" round="full">
                  <Image src="dk_icon.png" fit="contain" />
                </Box>
              )}
            </Box>
            <Box gap="xsmall">
              <Text truncate>{getTitle()}</Text>
              <Text truncate size="small">
                {getSubtitle()}
              </Text>
            </Box>
          </Box>
        </SHDLink>
        <Box align="end" justify="end" width={{ min: '29px' }}>
          {isAdmin && (
            <Button
              plain
              label={
                <Text weight="bold" color="tertiary-1">
                  Edit
                </Text>
              }
              onClick={() => {
                setShowEditProfile(true);
              }}
            />
          )}
        </Box>
      </Box>
      {showEditProfile && (
        <EditPlayerProfile
          teams={teams}
          closeLayer={() => {
            setShowEditProfile(false), history.push(`/${fullPlayer?._id}`);
          }}
          setShowEditSuccess={(updatedPlayer) => setFullPlayer(updatedPlayer)}
          claimedPlayer={fullPlayer}
        />
      )}
    </Card>
  );
};

export default ClaimedPlayerCard;
